import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  setGuestToken,
  setPendingInvitation,
  getPendingInvitation,
  removePendingInvitation,
} from "../../infrastructure/storage";
import {
  ApiError,
  type GroupInvitationResponseDto,
  type CreateInvitationDto,
  type InvitationInfoResponseDto,
  type JoinAsGuestDto,
  type GuestJoinResponseDto,
} from "@tabipla/shared";

// Query keys
const invitationKeys = {
  all: ["invitations"] as const,
  group: (groupId: string) => [...invitationKeys.all, groupId] as const,
  info: (code: string) => ["invitation-info", code] as const,
};

// Fetch invitations for a group (owner only)
export function useGroupInvitations(groupId: string | undefined) {
  return useQuery({
    queryKey: invitationKeys.group(groupId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<GroupInvitationResponseDto[]>(
        `/api/groups/${groupId}/invitations`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch invitations");
      }
      return response.data;
    },
    enabled: !!groupId,
  });
}

// Create invitation mutation
export function useCreateInvitation(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvitationDto) => {
      const response = await apiClient.post<GroupInvitationResponseDto>(
        `/api/groups/${groupId}/invitations`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create invitation");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.group(groupId) });
    },
  });
}

// Delete invitation mutation
export function useDeleteInvitation(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/invitations/${invitationId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete invitation");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.group(groupId) });
    },
  });
}

// Fetch invitation info (public, no auth required)
export function useInvitationInfo(code: string | undefined) {
  return useQuery({
    queryKey: invitationKeys.info(code ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<InvitationInfoResponseDto>(`/api/invitations/${code}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch invitation info");
      }
      return response.data;
    },
    enabled: !!code,
  });
}

interface UseJoinAsGuestOptions {
  /** Whether to store the guest token on success (for non-authenticated users) */
  storeToken?: boolean;
}

// Join as guest mutation (public, no auth required)
export function useJoinAsGuest(code: string, options: UseJoinAsGuestOptions = {}) {
  const { storeToken = true } = options;

  return useMutation({
    mutationFn: async (data: JoinAsGuestDto) => {
      const response = await apiClient.post<GuestJoinResponseDto>(
        `/api/invitations/${code}/join`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to join group");
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Store guest token if provided and storeToken is enabled
      if (storeToken && data?.token) {
        setGuestToken(data.token);
      }
    },
  });
}

// Helper hook for handling pending invitation flow
export function useHandlePendingInvitation() {
  return {
    setPendingInvitation,
    getPendingInvitation,
    removePendingInvitation,
  };
}
